<script setup lang="ts">
import {ref, onMounted} from 'vue';
import {useTimeAllocationCategoriesStore} from '@/stores/timeAllocationCategories.store.ts';
import {adminBackend} from '@/backend/admin.backend';
import useAppToast from '@/composables/useAppToast.js';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';

interface CategoryForm {
  key: string;
  label: string;
  description: string | null;
  color: string;
  order: number;
  active: boolean;
}

const loading = ref(false);
const saving = ref(false);
const categories = ref<CategoryForm[]>([]);
const editing = ref<CategoryForm | null>(null);
const dialogVisible = ref(false);
const toast = useAppToast();

const blank = (): CategoryForm => ({key: '', label: '', description: null, color: '#888888', order: categories.value.length, active: true});
const categoriesStore = useTimeAllocationCategoriesStore();

const load = async () => {
  loading.value = true;
  categories.value = await adminBackend.timeAllocationCategories() as any as CategoryForm[];
  loading.value = false;
}

onMounted(load);

const startNew = () => {
  editing.value = blank();
  dialogVisible.value = true;
};

const edit = (c: CategoryForm) => {
  editing.value = {...c};
  dialogVisible.value = true;
};

const cancel = () => { editing.value = null; dialogVisible.value = false; };

const save = async () => {
  if (!editing.value) return;
  saving.value = true;
  try {
  await adminBackend.saveTimeAllocationCategory(editing.value);
    toast.success('Saved');
  await load();
  categoriesStore.fetch(true); // force refresh global store
  editing.value = null;
  dialogVisible.value = false;
  } catch (e: any) {
    console.error(e);
    toast.error(e?.message || 'Failed to save');
  } finally {
    saving.value = false;
  }
};

// Deletion disabled per request; retain function stub if future reactivation needed
// const remove = async (c: CategoryForm) => { };
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-between items-center">
      <h3 class="text-lg font-semibold">Categories</h3>
      <Button label="New" icon="pi pi-plus" @click="startNew" />
    </div>

    <DataTable
        :value="categories" v-if="!loading" data-key="key" class="text-sm" size="small">
      <Column header="" :exportable="false" style="width: 1rem">
        <template #body="{ data }">
          <Button icon="pi pi-pencil" text severity="secondary" @click="edit(data)" />
        </template>
      </Column>
      <Column field="order" header="" style="width: 1rem"/>
      <Column field="label" header="Category">
        <template #body="{ data }">
          <span
              class="inline-flex items-start gap-2"
              :class="!data.active ? 'text-muted-color line-through' : ''"
          >
            <span class="w-4 h-4 rounded mt-1" :style="{background: data.color}"></span>
            <span class="flex flex-col gap-1">

              <span>{{ data.label }}</span>
              <span class="text-xs text-muted-color">{{ data.key }}</span>
            </span>
          </span>
        </template>
      </Column>
      <Column field="description" header="Description" />
    </DataTable>

    <div v-else class="text-center py-6">Loading...</div>

  <Dialog v-model:visible="dialogVisible" modal header="Category" :style="{width: '30rem'}" v-if="editing">
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold">Key</label>
          <InputText v-model="editing.key" :disabled="!!categories.find(c => c.key===editing!.key)" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold">Label</label>
          <InputText v-model="editing.label" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold">Description</label>
          <Textarea v-model="editing.description" rows="3" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold">Color</label>
          <InputText v-model="editing.color" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-semibold">Order</label>
          <InputNumber v-model="editing.order" :min="0" />
        </div>
        <div class="flex flex-row items-center gap-2">
          <Checkbox v-model="editing.active" :binary="true" />
          <label>Active</label>
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <Button label="Cancel" text @click="cancel" />
          <Button label="Save" icon="pi pi-save" @click="save" :loading="saving" />
        </div>
      </div>
    </Dialog>
  </div>
</template>
